import {
  MdArchive,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../components/utils';
import { usePlanContext } from '../../context/PlanContext';
import {
  precededByTrackRow,
  TimelineGroupLabelRow,
  TimelineRow,
  TimelineTrackRow,
} from '../track-rows';
import { TrackForEdit, TrackPreview } from '../types';
import { stylesForIndex } from '../utils';
import { TrackSidePanelItem } from './TrackSidePanelItem';

type ArchivedTracksSectionProps = {
  // Shared row model, used only to determine whether the first archived
  // parent should draw the inter-group border-t (see precededByTrackRow).
  rows: TimelineRow<TrackPreview>[];
  setTrackForEdit: (forEdit: TrackForEdit) => void;
  openAddTrackDrawer: (open: boolean, parentTrack?: TrackPreview) => void;
  refetchTracks: () => void;
  collapsedTracks: string[];
  toggleTrackCollapse: (trackId: string) => void;
  // Section expand/collapse state, owned by PlanContext (shared with
  // BlockPanel) so the row model and this section stay in sync.
  expanded: boolean;
  onToggleSection: () => void;
  // Global track-rows index of the "Archived Tracks (N)" header row.
  headerIndex: number;
  // How many groups the section holds: one per archived parent track, plus one
  // per still-active parent whose sub-track was archived on its own (the
  // section shows that group under a duplicated label). The active parents are
  // not themselves archived and are not counted — only the group is.
  archivedGroupCount: number;
  // Index of the final border-owning row in the whole shared row model — see
  // the comment in TrackPanel.tsx. Only the row at this index gets the
  // "end of section" border-b; otherwise the last archived row's own
  // border-b would stack with Additional Teams' border-t-4 divider when
  // that section follows. A collapsed group label can be that row.
  lastTrackIndex: number;
  // Opens the read-only "View Archived Track" drawer for a given track id.
  openViewArchivedTrackDrawer: (trackId: string) => void;
};

export function ArchivedTracksSection({
  rows,
  setTrackForEdit,
  openAddTrackDrawer,
  refetchTracks,
  collapsedTracks,
  toggleTrackCollapse,
  expanded,
  onToggleSection,
  headerIndex,
  archivedGroupCount,
  lastTrackIndex,
  openViewArchivedTrackDrawer,
}: ArchivedTracksSectionProps) {
  const { t } = useTranslation();
  // Deliberately the *unfiltered* archived list, not `filteredArchivedTracks`:
  // the rows below come from the shared model, and this list is only read as
  // `allTracks` so a row can resolve its parent. Scoping it to the athlete would
  // make a filtered-out archived parent look active, which withholds nothing and
  // instead offers an Unarchive the server rejects.
  const { archivedTracks, collapsedGroupLabelIds, toggleGroupLabelCollapsed } =
    usePlanContext();

  const sectionRows = rows.filter(
    (row): row is TimelineTrackRow<TrackPreview> | TimelineGroupLabelRow => {
      return (
        row.kind === 'group-label' || (row.kind === 'track' && row.isArchived)
      );
    },
  );

  const archivedTracksById = new Map(
    sectionRows
      .filter(
        (row): row is TimelineTrackRow<TrackPreview> => row.kind === 'track',
      )
      .map((row) => [row.track.id, row.track]),
  );

  return (
    <div className="flex flex-col flex-shrink-0">
      {/* The divider lives on this row (which has an explicit height from
          stylesForIndex), not the auto-height wrapper above — border-box
          sizing absorbs border-top-width here, so a thicker divider can't
          add extra flow height and drift out of sync with BlockPanel's
          fixed-height spacer for the same row. Only a top divider is drawn
          (closing the active section above); no border-b, since the first
          archived row's own background carries the boundary below. */}
      <button
        type="button"
        onClick={onToggleSection}
        className="flex flex-row w-full pl-6 pr-3 items-center text-left bg-transparent border-t-4"
        style={stylesForIndex(headerIndex + 1)}
        aria-expanded={expanded}
      >
        <MdArchive
          aria-hidden="true"
          size={18}
          className="mr-2 text-gray-500"
        />
        <span className="flex-grow font-semibold text-sm truncate">
          {t('archivedTracks', { trackCount: archivedGroupCount })}
        </span>
        {expanded ? (
          <MdKeyboardArrowUp size={18} className="text-gray-500" />
        ) : (
          <MdKeyboardArrowDown size={18} className="text-gray-500" />
        )}
      </button>
      {expanded &&
        sectionRows.map((row) => {
          // Stands in for the still-active parent's row, which is not in this
          // section: nothing about the parent is actionable from here. It is
          // this group's collapse toggle, so it hides the archived sub-tracks.
          if (row.kind === 'group-label') {
            const isCollapsed = collapsedGroupLabelIds.has(row.parentTrackId);
            return (
              <button
                key={`group-label-${row.parentTrackId}`}
                type="button"
                onClick={() => toggleGroupLabelCollapsed(row.parentTrackId)}
                aria-expanded={!isCollapsed}
                className={cn(
                  'flex flex-row w-full pl-6 pr-3 items-center text-left bg-gray-100',
                  row.index === lastTrackIndex && 'border-b',
                )}
                style={stylesForIndex(row.index + 1)}
              >
                <span className="flex-grow text-xs font-semibold text-gray-500 truncate">
                  {row.parentName}
                </span>
                {isCollapsed ? (
                  <MdKeyboardArrowDown size={16} className="text-gray-500" />
                ) : (
                  <MdKeyboardArrowUp size={16} className="text-gray-500" />
                )}
              </button>
            );
          }

          const track = row.track;
          const parentTrackId = track.parentTrackId;

          return (
            <TrackSidePanelItem
              key={track.id}
              track={track}
              allTracks={archivedTracks}
              setTrackForEdit={setTrackForEdit}
              index={row.index + 1}
              isLast={row.index === lastTrackIndex}
              isFirstInSection={!precededByTrackRow(rows, row.index)}
              parentTrack={
                row.isSubTrack && parentTrackId != null
                  ? archivedTracksById.get(parentTrackId)
                  : undefined
              }
              openAddTrackDrawer={openAddTrackDrawer}
              active={false}
              hasSubtracks={row.hasSubtracks}
              disabled={false}
              toggleCollapse={toggleTrackCollapse}
              isCollapsed={
                !row.isSubTrack && collapsedTracks.includes(track.id)
              }
              refetchTracks={refetchTracks}
              isArchived
              openViewArchivedTrackDrawer={openViewArchivedTrackDrawer}
            />
          );
        })}
    </div>
  );
}
